<template>
  <div class="topbar-container" data-aos="fade-down" data-aos-duration="1000">
    <div class="topbar-content">
      <!-- Contact button header with improved styling -->
      <div class="contact-header">
        <div class="contact-badge-container">
          <v-chip
            color="primary"
            outlined
            large
            class="contact-chip"
            @click="onShow"
          >
            <v-avatar left class="chip-avatar">
              <v-icon color="primary" size="20">mdi-email-heart</v-icon>
            </v-avatar>
            <span class="chip-text">Let's Work Together</span>
            <v-icon right size="16" class="chip-arrow">mdi-arrow-right</v-icon>
          </v-chip>
          <div class="availability-indicator">
            <div class="status-dot"></div>
            <span class="status-text">Available for projects</span>
          </div>
        </div>
      </div>

      <!-- Profile section with enhanced layout -->
      <div class="profile-section">
        <div class="profile-image-col" data-aos="fade-right" data-aos-delay="200">
          <div class="profile-image-container">
            <v-avatar class="profile-pic">
              <img src="../assets/cboy2.png" alt="Thomas Antwi - Software Engineer" />
            </v-avatar>
            <div class="profile-backdrop"></div>
            <div class="image-ring"></div>
          </div>
        </div>

        <div class="profile-details-col" data-aos="fade-left" data-aos-delay="400">
          <div class="developer-details">
            <div class="name-container">
              <h1 class="dev-name">Thomas Antwi</h1>
              <div class="name-underline"></div>
            </div>

            <div class="title-container">
              <span class="developer-position">
                <v-icon size="18" class="position-icon">mdi-code-tags</v-icon>
                <span class="position-text">Software Engineer</span>
              </span>
            </div>

            <div class="typing-container">
              <span class="typed-text">I build </span>
              <span class="changing-text">{{ currentText }}</span>
              <span class="cursor">|</span>
            </div>

            <div class="social-links">
              <div class="social-links-title">Connect with me:</div>
              <div class="social-buttons">
                <v-btn icon class="social-icon" href="https://linkedin.com/in/kalculus-guy-915192306" target="_blank">
                  <v-icon>mdi-linkedin</v-icon>
                </v-btn>
                <v-btn icon class="social-icon" href="https://github.com" target="_blank">
                  <v-icon>mdi-github</v-icon>
                </v-btn>
                <v-btn icon class="social-icon" href="https://t.me/tech_cg" target="_blank">
                  <v-icon>fab fa-telegram-plane</v-icon>
                </v-btn>
                <v-btn icon class="social-icon" href="mailto:calculus069@gmail.com">
                  <v-icon>mdi-email</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    onShow: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      texts: [
        "web applications",
        "responsive interfaces",
        "elegant solutions",
        "scalable systems",
        "user experiences"
      ],
      currentTextIndex: 0,
      currentText: "",
      isDeleting: false,
      typingSpeed: 100,
      deletingSpeed: 50,
      pauseTime: 1500
    };
  },
  mounted() {
    this.typeText();
  },
  methods: {
    typeText() {
      const currentPhrase = this.texts[this.currentTextIndex];
      const currentLength = this.currentText.length;

      // If deleting
      if (this.isDeleting) {
        this.currentText = currentPhrase.substring(0, currentLength - 1);
        this.typingSpeed = this.deletingSpeed;
      } else {
        // If typing
        this.currentText = currentPhrase.substring(0, currentLength + 1);
        this.typingSpeed = 100;
      }

      // If completed typing the phrase
      if (!this.isDeleting && this.currentText === currentPhrase) {
        this.typingSpeed = this.pauseTime; // Pause at the end
        this.isDeleting = true;
      } else if (this.isDeleting && this.currentText === '') {
        this.isDeleting = false;
        this.currentTextIndex = (this.currentTextIndex + 1) % this.texts.length;
      }

      setTimeout(() => this.typeText(), this.typingSpeed);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";
@import "@/styles/common.scss";

.topbar-container {
  width: 100%;
  position: relative;
  margin-bottom: 2rem;
}

.topbar-content {
  padding: 0;
  overflow: hidden;
  position: relative;
  transition: all $transition-medium;
  background: linear-gradient(
    135deg,
    rgba(67, 97, 238, 0.03) 0%,
    rgba(72, 149, 239, 0.03) 50%,
    rgba(67, 97, 238, 0.03) 100%
  );
  border-radius: 24px;
  border: 1px solid rgba(67, 97, 238, 0.08);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(67, 97, 238, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--v-background-base);
    opacity: 0.95;
    z-index: 0;
    border-radius: 24px;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: $breakpoint-xs) {
    margin: 0 0.5rem;
    border-radius: 20px;

    &::before {
      border-radius: 20px;
    }
  }
}

// Add floating orbs animation like hero section
.topbar-content::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle at 20% 30%,
    rgba(67, 97, 238, 0.05) 0%,
    transparent 50%
  ),
  radial-gradient(
    circle at 80% 70%,
    rgba(72, 149, 239, 0.05) 0%,
    transparent 50%
  ),
  radial-gradient(
    circle at 60% 20%,
    rgba(67, 97, 238, 0.03) 0%,
    transparent 50%
  );
  z-index: 0;
  animation: subtleFloat 15s ease-in-out infinite;
  border-radius: 50%;
}

@keyframes subtleFloat {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.3;
  }
  33% {
    transform: translateY(-10px) rotate(120deg);
    opacity: 0.5;
  }
  66% {
    transform: translateY(10px) rotate(240deg);
    opacity: 0.4;
  }
}

.contact-header {
  display: flex;
  justify-content: flex-start;
  padding: 1.5rem 2rem 0;

  @media (max-width: $breakpoint-md) {
    justify-content: flex-start;
    padding: 1.5rem 1.5rem 0;
  }

  @media (max-width: $breakpoint-xs) {
    padding: 1rem 1rem 0;
  }
}

.contact-badge-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;

  @media (max-width: $breakpoint-md) {
    align-items: flex-start;
  }
}

.contact-chip {
  cursor: pointer;
  transition: all $transition-medium;
  height: 48px !important;
  border-radius: 24px !important;
  border-width: 2px !important;
  backdrop-filter: blur(10px);
  background: rgba(67, 97, 238, 0.03) !important;

  .chip-avatar {
    background: rgba(67, 97, 238, 0.1) !important;
    width: 32px !important;
    height: 32px !important;
  }

  .chip-text {
    font-weight: 600;
    font-size: 1rem;
    letter-spacing: 0.5px;

    @media (max-width: 480px) {
      font-size: 0.8rem;
      letter-spacing: 0.3px;
    }
  }

  .chip-arrow {
    transition: all $transition-medium;
  }

  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(67, 97, 238, 0.2);
    background: rgba(67, 97, 238, 0.08) !important;
    border-color: var(--v-primary-base) !important;

    .chip-avatar {
      background: var(--v-primary-base) !important;

      .v-icon {
        color: white !important;
      }
    }

    .chip-arrow {
      transform: translateX(3px);
    }
  }

  @media (max-width: 480px) {
    height: 40px !important;
    border-radius: 20px !important;
    font-size: 0.85rem;
    padding: 0 12px !important;
    min-width: auto !important;

    .chip-avatar {
      margin-left: 0 !important;
      margin-right: 0 !important;
      width: 28px !important;
      height: 28px !important;

      .v-icon {
        font-size: 16px !important;
      }
    }

    .chip-arrow {
      font-size: 14px !important;
    }
  }
}

.availability-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.5rem;

  @media (max-width: $breakpoint-md) {
    margin-left: 0;
  }

  @media (max-width: 480px) {
    display: none;
  }
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4CAF50;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.2);
  }
}

.status-text {
  font-size: 0.8rem;
  color: var(--v-textSecondary-base);
  font-weight: 500;
  opacity: 0.8;
}

.profile-section {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  align-items: center;
  gap: 2rem;
  padding: 2rem;

  @media (max-width: $breakpoint-md) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 1.5rem;
    text-align: center;
  }

  @media (max-width: $breakpoint-xs) {
    padding: 1rem;
    gap: 1.5rem;
  }
}

.profile-image-col {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.profile-image-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.profile-pic {
  position: relative;
  z-index: 3;
  border: 3px solid var(--v-primary-base);
  box-shadow: 0 10px 30px rgba(67, 97, 238, 0.2);
  transition: all $transition-medium;

  @media (min-width: $breakpoint-md) {
    width: 220px !important;
    height: 220px !important;
  }

  @media (max-width: $breakpoint-md) {
    width: 180px !important;
    height: 180px !important;
  }

  @media (max-width: $breakpoint-xs) {
    width: 140px !important;
    height: 140px !important;
  }

  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 15px 40px rgba(67, 97, 238, 0.3);
  }
}

.profile-backdrop {
  position: absolute;
  top: 15px;
  left: 15px;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.1), rgba(72, 149, 239, 0.1));
  border-radius: 50%;
  z-index: 1;

  @media (max-width: $breakpoint-xs) {
    top: 10px;
    left: 10px;
  }
}

.image-ring {
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  border: 2px solid transparent;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(72, 149, 239, 0.15));
  z-index: 0;
  animation: pulse-ring 3s ease-in-out infinite;

  @media (max-width: $breakpoint-xs) {
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
  }
}

@keyframes pulse-ring {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

.profile-details-col {
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: $breakpoint-sm) {
    margin-top: 2rem;
    align-items: center;
    text-align: center;
  }
}

.developer-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 500px;

  @media (max-width: $breakpoint-md) {
    align-items: center;
    max-width: 100%;
  }
}

.name-container {
  position: relative;
  margin-bottom: 0.5rem;
}

.dev-name {
  color: var(--v-textPrimary-base);
  font-weight: 800;
  margin: 0;
  line-height: 1.2;

  @media (min-width: $breakpoint-sm) {
    font-size: 3rem;
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 2rem;
  }
}

.name-underline {
  width: 80px;
  height: 4px;
  background-color: var(--v-primary-base);
  margin-top: 0.5rem;
  border-radius: 2px;

  @media (max-width: $breakpoint-sm) {
    margin: 0.5rem auto;
  }
}

.title-container {
  margin-top: 0.5rem;
}

.developer-position {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--v-primary-base);
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.1), rgba(72, 149, 239, 0.05));
  border: 1px solid rgba(67, 97, 238, 0.15);
  border-radius: 25px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  backdrop-filter: blur(10px);
  transition: all $transition-medium;

  .position-icon {
    color: var(--v-primary-base);
  }

  .position-text {
    position: relative;
    font-size: 1rem;

    @media (min-width: $breakpoint-md) {
      font-size: 1.1rem;
    }

    @media (max-width: $breakpoint-xs) {
      font-size: 0.95rem;
    }
  }

  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(72, 149, 239, 0.1));
    border-color: rgba(67, 97, 238, 0.25);
    box-shadow: 0 8px 25px rgba(67, 97, 238, 0.15);
  }
}

.typing-container {
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--v-textSecondary-base);

  @media (max-width: $breakpoint-xs) {
    font-size: 1.2rem;
  }
}

.typed-text {
  opacity: 0.9;
}

.changing-text {
  color: var(--v-primary-base);
  font-weight: 600;
}

.cursor {
  display: inline-block;
  width: 3px;
  background-color: var(--v-primary-base);
  animation: blink 1s infinite;
}

.social-links {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.social-links-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-textSecondary-base);
  opacity: 0.8;
}

.social-buttons {
  display: flex;
  gap: 1rem;

  @media (max-width: $breakpoint-xs) {
    justify-content: center;
  }
}

.social-icon {
  transition: all $transition-medium;
  background: rgba(67, 97, 238, 0.05);
  border-radius: 50%;

  &:hover {
    transform: translateY(-3px) scale(1.1);
    color: var(--v-primary-base) !important;
    background: rgba(67, 97, 238, 0.1);
    box-shadow: 0 6px 20px rgba(67, 97, 238, 0.2);
  }
}

@keyframes blink {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0;
  }
}

// Additional responsive overrides
@media (max-width: $breakpoint-xs) {
  .typing-container {
    font-size: 1.1rem;
  }

  .dev-name {
    font-size: 1.8rem !important;
  }

  .name-underline {
    width: 60px;
    margin: 0.5rem auto;
  }

  .developer-position {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }

  .social-buttons {
    gap: 0.5rem;
  }
}
</style>